import React, { useCallback, useEffect, useState } from 'react';
import { QuizQuestion } from './QuizQuestion';
import { QuizResult } from './QuizResult';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
interface QuizProps {
  onComplete: (score: number) => void;
  onQuizProgress: (questionNumber: number) => void;
  autoStart?: boolean;
}
export function Quiz({
  onComplete,
  onQuizProgress,
  autoStart = false
}: QuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [completed, setCompleted] = useState(false);
  const [quizStarted, setQuizStarted] = useState(autoStart);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  // Auto-start the quiz if the autoStart prop is true
  useEffect(() => {
    if (autoStart) {
      setQuizStarted(true);
      onQuizProgress(0);
    }
  }, [autoStart, onQuizProgress]);
  const questions = [{
    question: 'Var bor du främst?',
    options: [{
      text: 'Hus',
      points: 1
    }, {
      text: 'Lägenhet',
      points: 1
    }, {
      text: 'Fritidshus/semesterbostad',
      points: 2
    }],
    insight: 'Fritidshus står ofta tomma långa perioder – extra sårbart.',
    source: 'Källa: SSF/Brå'
  }, {
    question: 'Hur säkra känns dina entréer/fönster?',
    options: [{
      text: 'Mycket säkra',
      points: 0
    }, {
      text: 'Osäker på lås/beslag',
      points: 1
    }, {
      text: 'Flera äldre fönster/dörrar',
      points: 2
    }],
    insight: 'Många inbrott sker via dörr/fönster – förstärkning minskar risken.',
    source: 'Källa: Brå/Polisen'
  }, {
    question: 'Om ett larm går – vem agerar först?',
    options: [{
      text: 'Jag eller granne',
      points: 1
    }, {
      text: 'Ingen, vi har inget larm',
      points: 2
    }, {
      text: 'Larmcentral med operatörer',
      points: 0
    }],
    insight: 'Professionell övervakning innebär att operatörer kan agera inom sekunder.',
    source: 'Källa: Verisure'
  }, {
    question: 'Har du brandlarm som är kopplat till en larmcentral?',
    options: [{
      text: 'Ja',
      points: 0
    }, {
      text: 'Vanliga rökvarnare',
      points: 1
    }, {
      text: 'Osäker/inget',
      points: 2
    }],
    insight: 'Rök kan fylla en bostad mycket snabbt – tidig åtgärd räknas mest.',
    source: 'Källa: MSB'
  }, {
    question: 'Hur ofta står bostaden tom (dag/kväll)?',
    options: [{
      text: 'Sällan',
      points: 0
    }, {
      text: 'Ibland',
      points: 1
    }, {
      text: 'Ofta',
      points: 2
    }],
    insight: 'Tom bostad = högre exponering. Kameradetektering & skyltning avskräcker.',
    source: 'Källa: SSF/Brå'
  }, {
    question: 'Har du digitalt lås, kameradetektering eller skalskydd?',
    options: [{
      text: 'Flera av ovan',
      points: 0
    }, {
      text: 'Något av ovan',
      points: 1
    }, {
      text: 'Inget av ovan',
      points: 2
    }],
    insight: 'Kombinerat skydd (lås + sensorer + övervakning) ger störst effekt.',
    source: 'Källa: SSF/Polisen'
  }];
  const handleAnswer = useCallback((points: number) => {
    // Store the answer
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = points;
    setAnswers(newAnswers);
    // Update the score
    const newScore = score + points;
    setScore(newScore);
    // Animate transition
    setIsAnimating(true);
    setDirection('next');
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        const nextQuestion = currentQuestion + 1;
        setCurrentQuestion(nextQuestion);
        onQuizProgress(nextQuestion);
      } else {
        setCompleted(true);
        onComplete(newScore);
      }
      setIsAnimating(false);
    }, 500);
  }, [currentQuestion, score, answers, questions.length, onComplete, onQuizProgress]);
  const goToPreviousQuestion = useCallback(() => {
    if (currentQuestion > 0) {
      setIsAnimating(true);
      setDirection('prev');
      setTimeout(() => {
        // Subtract the points from the previous answer
        setScore(score - answers[currentQuestion]);
        // Go to previous question
        setCurrentQuestion(currentQuestion - 1);
        onQuizProgress(currentQuestion - 1);
        setIsAnimating(false);
      }, 500);
    }
  }, [currentQuestion, score, answers, onQuizProgress]);
  const startQuiz = useCallback(() => {
    setQuizStarted(true);
    setCurrentQuestion(0);
    setScore(0);
    setAnswers([]);
    setCompleted(false);
    onQuizProgress(0);
  }, [onQuizProgress]);
  // If the quiz hasn't started yet, show the start button
  if (!quizStarted) {
    return <section id="quiz" className="py-10 px-4 bg-white" data-id="element-167">
        <div className="container mx-auto max-w-4xl" data-id="element-168">
          <div className="bg-white rounded-lg p-8 shadow-lg border border-gray-100" data-id="element-169">
            <h2 className="text-2xl font-bold text-center mb-6" data-id="element-170">
              Säkerhetsquiz
            </h2>
            <p className="text-center mb-8 max-w-md mx-auto" data-id="element-171">
              Svara på 6 snabba frågor och se hur säkert ditt hem är. Du får en
              personlig bedömning och rekommendationer.
            </p>
            <div className="flex justify-center" data-id="element-172">
              <button onClick={startQuiz} className="bg-red-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition-all flex items-center shadow-md hover:shadow-lg" data-id="element-173">
                Starta quizet
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-174" />
              </button>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-center" data-id="element-175">
              <div className="p-3 bg-gray-50 rounded-lg" data-id="element-176">
                <div className="font-bold text-red-600 text-xl" data-id="element-177">6</div>
                <div className="text-sm" data-id="element-178">snabba frågor</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg" data-id="element-179">
                <div className="font-bold text-red-600 text-xl" data-id="element-180">60</div>
                <div className="text-sm" data-id="element-181">sekunder</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg" data-id="element-182">
                <div className="font-bold text-red-600 text-xl" data-id="element-183">100%</div>
                <div className="text-sm" data-id="element-184">gratis</div>
              </div>
            </div>
          </div>
        </div>
      </section>;
  }
  return <section id="quiz" className="py-10 px-4 bg-white" data-id="element-185">
      <div className="container mx-auto max-w-4xl" data-id="element-186">
        <h2 className="text-2xl font-bold text-center mb-8" data-id="element-187">Säkerhetsquiz</h2>
        {!completed ? <div data-id="element-188">
            {/* Progress bar */}
            <div className="sticky top-0 bg-white py-2 z-10 shadow-sm" data-id="element-189">
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-190">
                <div className="bg-red-600 h-2.5 rounded-full transition-all duration-500 ease-in-out" style={{
              width: `${(currentQuestion + 1) / questions.length * 100}%`
            }} data-id="element-191"></div>
              </div>
              <div className="flex justify-between items-center mt-2" data-id="element-192">
                <button onClick={goToPreviousQuestion} disabled={currentQuestion === 0} className={`text-sm flex items-center ${currentQuestion === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-700'}`} data-id="element-193">
                  <ArrowLeftIcon className="h-3 w-3 mr-1" data-id="element-194" />
                  Föregående
                </button>
                <p className="text-sm text-center" data-id="element-195">
                  Fråga {currentQuestion + 1} av {questions.length}
                </p>
                <div className="text-sm text-transparent" data-id="element-196">Placeholder</div>
              </div>
            </div>
            <div className={`transition-all duration-500 transform ${isAnimating ? direction === 'next' ? 'translate-x-full opacity-0' : '-translate-x-full opacity-0' : 'translate-x-0 opacity-100'}`} data-id="element-197">
              <QuizQuestion key={currentQuestion} question={questions[currentQuestion].question} options={questions[currentQuestion].options} onAnswer={handleAnswer} insight={questions[currentQuestion].insight} source={questions[currentQuestion].source} questionNumber={currentQuestion + 1} totalQuestions={questions.length} data-id="element-198" />
            </div>
          </div> : <QuizResult score={score} data-id="element-199" />}
      </div>
    </section>;
}